import React, { useState } from "react"
import { Row, Col, FormGroup, Label, Button, Tooltip } from "reactstrap"
import { useTranslation } from "react-i18next"
import { Formik, Form as FormikForm } from "formik"
import * as yup from "yup"
import Select from "react-select"
import { AddMedicineFormData } from "../medicineTypes"
import { useListQuery } from "helpers/http-client/hooks/useCrud"
import { API_ENDPOINTS } from "helpers/http-client/httpConstants"
import { GenericFormModal } from "components/Modals/AddGenericModal"
import { FormType } from "components/Modals/formTypes"

interface Option {
  value: string
  label: string
  data?: any
}

const validationSchema = yup.object({
  manufacturerId: yup.string().required("Manufacturer is required"),
  categoryId: yup.string().required("Category is required"),
  medicineIngredients: yup
    .array()
    .of(
      yup.object({
        ingredientId: yup.string().required("Ingredient ID is required"),
        concentration: yup.string().nullable(),
      })
    )
    .min(1, "At least one active ingredient is required"),
})

interface ClassificationFormProps {
  formData: AddMedicineFormData
  onNext: (values: Partial<AddMedicineFormData>) => void
  onBack: () => void
  onSkip: () => void
  onSkipAll: () => void
  isUrdu: boolean
}

export const ClassificationForm: React.FC<ClassificationFormProps> = ({
  formData,
  onNext,
  onBack,
  onSkip,
  onSkipAll,
  isUrdu,
}) => {
  const { t } = useTranslation()

  const [tooltipOpen, setTooltipOpen] = useState<{ [key: string]: boolean }>({})
  const toggleTooltip = (field: string) =>
    setTooltipOpen(prev => ({ ...prev, [field]: !prev[field] }))
  const [selectedManufacturer, setSelectedManufacturer] = useState<Option | null>(null)
  const [selectedCategory, setSelectedCategory] = useState<Option | null>(null)
  const [ingredientModalOpen, setIngredientModalOpen] = useState(false)

  const {
    data: manufacturerOptions,
    isLoading: isLoadingManufacturers,
    isError: isErrorManufacturers,
  } = useListQuery(API_ENDPOINTS.MANUFACTURERS)
  const {
    data: categoryOptions,
    isLoading: isLoadingCategories,
    isError: isErrorCategories,
  } = useListQuery(API_ENDPOINTS.CATEGORIES)
  const {
    data: ingredientOptions,
    isLoading: isLoadingIngredients,
    isError: isErrorIngredients,
  } = useListQuery(API_ENDPOINTS.ACTIVE_INGREDIENTS)

  const handleAddIngredientSuccess = (newIngredient: any) => {
    const newOption = {
      value: newIngredient.ingredientId,
      label: newIngredient.name,
    }
    setIngredientModalOpen(false)
  }

  return (
    <>
      <Formik
        initialValues={{
          manufacturerId: formData.manufacturerId || "",
          categoryId: formData.categoryId || "",
          class: formData.class || "",
          medicineIngredients: formData.medicineIngredients || [],
        }}
        validationSchema={validationSchema}
        onSubmit={values => onNext(values)}
        enableReinitialize
      >
        {({
          errors,
          touched,
          isValid,
          dirty,
          setFieldValue,
          values,
          handleBlur,
        }) => (
          <FormikForm dir={isUrdu ? "rtl" : "ltr"} className="p-3">
            <Row>
              <Col lg={6}>
                <FormGroup className="mb-3">
                  <Label
                    htmlFor="manufacturerId"
                    className={isUrdu ? "fw-bold" : ""}
                  >
                    {t("Manufacturer")} *{" "}
                    <span
                      id="manufacturerTooltip"
                      className="text-info"
                      style={{ cursor: "pointer" }}
                    >
                      ?
                    </span>
                  </Label>
                  <Tooltip
                    placement="top"
                    isOpen={tooltipOpen.manufacturerId}
                    target="manufacturerTooltip"
                    toggle={() => toggleTooltip("manufacturerId")}
                  >
                    {t("Select the manufacturer of the medicine.")}
                  </Tooltip>
                  {isLoadingManufacturers ? (
                    <div className="form-control disabled bg-light d-flex align-items-center">
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                      />
                      {t("Loading manufacturers...")}
                    </div>
                  ) : isErrorManufacturers ? (
                    <div className="text-danger">
                      {t("Failed to load manufacturers. Please try again.")}
                    </div>
                  ) : (
                    <Select
                      inputId="manufacturerId"
                      name="manufacturerId"
                      options={manufacturerOptions?.map((m: any) => ({
                        value: m.manufacturerId,
                        label: m.name,
                      }))}
                      placeholder={t("Select Manufacturer")}
                      value={selectedManufacturer}
                      onChange={option => {
                        setFieldValue(
                          "manufacturerId",
                          option ? option.value : ""
                        )
                        setSelectedManufacturer(option)
                      }}
                      onBlur={handleBlur}
                      styles={{
                        control: base => ({
                          ...base,
                          textAlign: isUrdu ? "right" : "left",
                          borderColor:
                            touched.manufacturerId && errors.manufacturerId
                              ? "#dc3545"
                              : base.borderColor,
                        }),
                        singleValue: base => ({
                          ...base,
                          textAlign: isUrdu ? "right" : "left",
                          direction: isUrdu ? "rtl" : "ltr",
                        }),
                      }}
                      className={
                        touched.manufacturerId && errors.manufacturerId
                          ? "is-invalid"
                          : ""
                      }
                      isRtl={isUrdu}
                      isClearable
                    />
                  )}
                  {touched.manufacturerId && errors.manufacturerId && (
                    <div className="invalid-feedback d-block">
                      {errors.manufacturerId}
                    </div>
                  )}
                </FormGroup>
              </Col>

              <Col lg={6}>
                <FormGroup className="mb-3">
                  <Label htmlFor="categoryId" className={isUrdu ? "fw-bold" : ""}>
                    {t("Category")} *{" "}
                    <span
                      id="categoryTooltip"
                      className="text-info"
                      style={{ cursor: "pointer" }}
                    >
                      ?
                    </span>
                  </Label>
                  <Tooltip
                    placement="top"
                    isOpen={tooltipOpen.categoryId}
                    target="categoryTooltip"
                    toggle={() => toggleTooltip("categoryId")}
                  >
                    {t("Select the category of the medicine, e.g., Antibiotic.")}
                  </Tooltip>
                  {isLoadingCategories ? (
                    <div className="form-control disabled bg-light d-flex align-items-center">
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                      />
                      {t("Loading categories...")}
                    </div>
                  ) : isErrorCategories ? (
                    <div className="text-danger">
                      {t("Failed to load categories. Please try again.")}
                    </div>
                  ) : (
                    <Select
                      inputId="categoryId"
                      name="categoryId"
                      options={categoryOptions?.map((c: any) => ({
                        value: c.categoryId,
                        label: c.name,
                      }))}
                      placeholder={t("Select Category")}
                      value={selectedCategory}
                      onChange={option => {
                        setFieldValue("categoryId", option ? option.value : "")
                        setSelectedCategory(option)
                      }}
                      onBlur={handleBlur}
                      styles={{
                        control: base => ({
                          ...base,
                          textAlign: isUrdu ? "right" : "left",
                          borderColor:
                            touched.categoryId && errors.categoryId
                              ? "#dc3545"
                              : base.borderColor,
                        }),
                        singleValue: base => ({
                          ...base,
                          textAlign: isUrdu ? "right" : "left",
                          direction: isUrdu ? "rtl" : "ltr",
                        }),
                      }}
                      className={
                        touched.categoryId && errors.categoryId
                          ? "is-invalid"
                          : ""
                      }
                      isRtl={isUrdu}
                      isClearable
                    />
                  )}
                  {touched.categoryId && errors.categoryId && (
                    <div className="invalid-feedback d-block">
                      {errors.categoryId}
                    </div>
                  )}
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col lg={6}>
                <FormGroup className="mb-3">
                  <Label htmlFor="class" className={isUrdu ? "fw-bold" : ""}>
                    {t("Class")} <span id="classTooltip" className="text-info">?</span>
                  </Label>
                  <Tooltip
                    placement="top"
                    isOpen={tooltipOpen.class}
                    target="classTooltip"
                    toggle={() => toggleTooltip("class")}
                  >
                    {t("Specify the class of the medicine, e.g., Analgesic.")}
                  </Tooltip>
                  <input
                    name="class"
                    className="form-control"
                    placeholder={t("e.g., Analgesic")}
                    value={values.class}
                    onChange={(e) => setFieldValue("class", e.target.value)}
                    onBlur={handleBlur}
                    style={{ textAlign: isUrdu ? "right" : "left" }}
                  />
                </FormGroup>
              </Col>

              <Col lg={6}>
                <FormGroup className="mb-3">
                  <Label
                    htmlFor="medicineIngredients"
                    className={isUrdu ? "fw-bold" : ""}
                  >
                    {t("Active Ingredients")} *{" "}
                    <span id="ingredientsTooltip" className="text-info">?</span>
                  </Label>
                  <Tooltip
                    placement="top"
                    isOpen={tooltipOpen.medicineIngredients}
                    target="ingredientsTooltip"
                    toggle={() => toggleTooltip("medicineIngredients")}
                  >
                    {t("Select the active ingredients of the medicine.")}
                  </Tooltip>
                  <div className="d-flex gap-2">
                    <div className="flex-grow-1">
                      {isLoadingIngredients ? (
                        <div className="form-control disabled bg-light d-flex align-items-center">
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                          />
                          {t("Loading ingredients...")}
                        </div>
                      ) : isErrorIngredients ? (
                        <div className="text-danger">
                          {t("Failed to load ingredients. Please try again.")}
                        </div>
                      ) : (
                        <Select
                          isMulti
                          inputId="medicineIngredients"
                          name="medicineIngredients"
                          options={ingredientOptions?.map((i: any) => ({
                            value: i.ingredientId,
                            label: i.name,
                            data: i,
                          }))}
                          placeholder={t("Select Active Ingredients")}
                          value={values.medicineIngredients?.map((ing: any) => ({
                            value: ing.ingredientId,
                            label:
                              ingredientOptions?.find(
                                (i: any) => i.ingredientId === ing.ingredientId
                              )?.name || ing.ingredientId,
                            data: ing,
                          }))}
                          onChange={(selected: any) => {
                            const newIngredients = selected.map((s: any) => ({
                              ingredientId: s.value,
                              concentration:
                                values.medicineIngredients?.find(
                                  (i: any) => i.ingredientId === s.value
                                )?.concentration || "",
                            }))
                            setFieldValue("medicineIngredients", newIngredients)
                          }}
                          onBlur={handleBlur}
                          styles={{
                            control: base => ({
                              ...base,
                              textAlign: isUrdu ? "right" : "left",
                              borderColor:
                                touched.medicineIngredients &&
                                errors.medicineIngredients
                                  ? "#dc3545"
                                  : base.borderColor,
                            }),
                            singleValue: base => ({
                              ...base,
                              textAlign: isUrdu ? "right" : "left",
                              direction: isUrdu ? "rtl" : "ltr",
                            }),
                            multiValueLabel: base => ({
                              ...base,
                              whiteSpace: "normal",
                            }),
                          }}
                          className={
                            touched.medicineIngredients &&
                            errors.medicineIngredients
                              ? "is-invalid"
                              : ""
                          }
                          isRtl={isUrdu}
                          isClearable
                          formatOptionLabel={({ label, data }) => (
                            <div style={{ display: "flex", flexDirection: "column" }}>
                              <div style={{ fontWeight: "600" }}>{label}</div>
                              {data?.concentration && (
                                <div style={{ fontSize: 12, color: "#666", marginTop: 2 }}>
                                  {t("Concentration")}: {data.concentration}
                                </div>
                              )}
                            </div>
                          )}
                        />
                      )}
                      {touched.medicineIngredients && errors.medicineIngredients && (
                        <div className="invalid-feedback d-block">
                          {typeof errors.medicineIngredients === "string"
                            ? t(errors.medicineIngredients)
                            : t("At least one active ingredient is required")}
                        </div>
                      )}
                    </div>
                    <Button
                      color="primary"
                      size="sm"
                      onClick={() => setIngredientModalOpen(true)}
                      title={t("Add Ingredient")}
                    >
                      +
                    </Button>
                  </div>
                </FormGroup>
              </Col>
            </Row>

            <div className="d-flex justify-content-between mt-4">
              <div>
                <Button
                  color="secondary"
                  onClick={onBack}
                  className={isUrdu ? "fw-bold" : "me-2"}
                >
                  {t("Back")}
                </Button>
                <Button
                  color="warning"
                  onClick={onSkip}
                  className={isUrdu ? "fw-bold" : ""}
                >
                  {t("Skip")}
                </Button>
              </div>
              <div>
                <Button
                  color="secondary"
                  onClick={onSkipAll}
                  className={isUrdu ? "fw-bold" : "me-2"}
                >
                  {t("Skip All")}
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  disabled={!(isValid && dirty)}
                  className={isUrdu ? "fw-bold" : ""}
                >
                  {t("Next")}
                </Button>
              </div>
            </div>
          </FormikForm>
        )}
      </Formik>

      <GenericFormModal
        isOpen={ingredientModalOpen}
        toggle={() => setIngredientModalOpen(false)}
        formType={FormType.ACTIVE_INGREDIENT}
        onSuccess={handleAddIngredientSuccess}
      />
    </>
  )
}